import React, { PropsWithChildren, useEffect } from 'react';
import { Layout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Cookies from 'js-cookie';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import WebFont from 'webfontloader';

import { AddCandidatePage } from './features/candidate-details/AddCandidatePage';
import { CandidateCvSuggestionsPage } from './features/candidate-details/CandidateCvSuggestionsPage';
import { EditCandidatePage } from './features/candidate-details/EditCandidatePage';
import { MatchingAdminPage } from './features/candidate-project-matching/admin/MatchingAdminPage';
import { MatchDetailsPage } from './features/candidate-project-matching/MatchDetailsPage';
import { RecommendationsPage } from './features/candidate-project-matching/RecommendationsPage';
import { CandidatesPage } from './features/candidates/CandidatesPage';
import { AddClientPage } from './features/client-details/AddClientPage';
import { EditClientPage } from './features/client-details/EditClientPage';
import { ClientsPage } from './features/clients/ClientsPage';
import { PublicConsentsPage } from './features/consents/PublicConsentsPage';
import { EmailConvertPage } from './features/convert-to-candidate/ConvertToCandidatePage';
import { DictionariesPage } from './features/dictionaries/DictionariesPage';
import { EmailDetails } from './features/email-details/EmailDetails';
import { EmailsPage } from './features/emails/EmailsPage';
import { Login } from './features/login/LoginPage';
import { Navigation } from './features/login/Navigation';
import { RequireAdmin } from './features/login/RequireAdmin';
import { AddProjectPage } from './features/project-details/AddProjectPage';
import { EditProjectPage } from './features/project-details/EditProjectPage';
import { ProjectsPage } from './features/projects/ProjectsPage';
import { RecruitmentDetailsPage } from './features/recruitment-details/RecruitmentDetailsPage';
import { RecruitmentsPage } from './features/recruitments/RecruitmentsPage';
import { RemindersPage } from './features/reminders/RemindersPage';
import TranslateProvider from './features/translation/TranslationProvider';
import Icon from './icons/Icon';
import { useQueryClientt } from './utils/query-client';
import { PageRoutesEnum } from './utils/routes';
import i18n from './i18n';

import 'antd/dist/reset.css';

const StyledContent = styled(Content)`
  background-color: #f7fafc;
  min-height: 100vh;
  margin-top: 64px;

  @media (min-width: 576px) {
    margin-left: 95px;
    margin-top: 0;
  }
`;

const Background = styled.div`
  position: fixed;
  top: calc(100% - 180px);
  left: calc(100% - 260px);
  height: 325px;
  width: 325px;
  background-image: url(image);
`;

export interface ProtectedLayoutProps extends PropsWithChildren {
  redirectPath?: string;
}

const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ redirectPath = PageRoutesEnum.login }) => {
  const token = Cookies.get('token');
  if (!token) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <Layout style={{ background: '#F7FAFC' }}>
      <Navigation />
      <StyledContent>
        <Background>
          <Icon name="Subtraction" />
        </Background>
        <Outlet />
      </StyledContent>
      <ReactQueryDevtools initialIsOpen={false} />
    </Layout>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    WebFont.load({
      google: {
        families: ['Lato', 'Lato Black'],
      },
    });
  }, []);

  const queryClient = useQueryClientt();

  return (
    <QueryClientProvider client={queryClient}>
      <Layout>
        <TranslateProvider waitForTranslations i18n={i18n}>
          <Routes>
            <Route element={<ProtectedLayout />}>
              <Route path={PageRoutesEnum.main} element={<Navigate replace to={PageRoutesEnum.candidates} />} />
              <Route path={PageRoutesEnum.candidates}>
                <Route path="" element={<CandidatesPage />} />
                <Route path=":id" element={<EditCandidatePage />} />
                <Route path="add" element={<AddCandidatePage />} />
              </Route>

              <Route path={PageRoutesEnum.recruitments}>
                <Route path="" element={<RecruitmentsPage />} />
                <Route path=":id" element={<RecruitmentDetailsPage />} />
              </Route>

              <Route path={PageRoutesEnum.emails}>
                <Route path="" element={<EmailsPage />} />
                <Route path=":id" element={<EmailDetails />} />
              </Route>

              <Route path={PageRoutesEnum.emailConvert}>
                <Route path=":id" element={<EmailConvertPage />} />
              </Route>
              <Route path={PageRoutesEnum.clients}>
                <Route path="" element={<ClientsPage />} />
                <Route path="add" element={<AddClientPage />} />
                <Route path=":id" element={<EditClientPage />} />
              </Route>
              <Route path={PageRoutesEnum.projects}>
                <Route path="" element={<ProjectsPage />} />
                <Route path="add" element={<AddProjectPage />} />
                <Route path=":id" element={<EditProjectPage />} />
              </Route>
              <Route path={PageRoutesEnum.candidateCvSuggestions} element={<CandidateCvSuggestionsPage />} />
              <Route path={PageRoutesEnum.candidateMatches} element={<RecommendationsPage sourceType="candidate" />} />
              <Route path={PageRoutesEnum.projectMatches} element={<RecommendationsPage sourceType="project" />} />
              <Route
                path={PageRoutesEnum.candidateMatchDetails}
                element={<MatchDetailsPage sourceType="candidate" />}
              />
              <Route path={PageRoutesEnum.projectMatchDetails} element={<MatchDetailsPage sourceType="project" />} />
              <Route
                path={PageRoutesEnum.matchingAdmin}
                element={
                  <RequireAdmin>
                    <MatchingAdminPage />
                  </RequireAdmin>
                }
              />
              <Route path={PageRoutesEnum.reminders} element={<RemindersPage />} />
              <Route path={PageRoutesEnum.dictionaries} element={<DictionariesPage />} />
              {/* TODO: Add dashboard view */}
              <Route path={PageRoutesEnum.dashboard} element={<CandidatesPage />} />
            </Route>

            <Route path={PageRoutesEnum.login} element={<Login />} />

            <Route path={PageRoutesEnum.consents} element={<PublicConsentsPage />} />
          </Routes>
        </TranslateProvider>
      </Layout>
    </QueryClientProvider>
  );
};

export default App;
